
import io from "socket.io-client";
import { decompressGzipBase64 } from "./utils";
import {
    handleUpdatedAndNewRowsDataFromSocket,
    handleSubsheetDataUpdationThroughFormViaSocket,
    handleCommentsUpdationFromSocket,
    handleRowDeletionFromSocket,
    handleAddEditColumnFromSocket,
    handleDeleteColumnFromSocket
} from "./eventReceiver";
import ApiClient from "utils/ApiClient";
import {handleSocketConnection, checkProperConnectionViaPingPong} from "./eventEmitter";

function setTokenInSocketAuth(token) {
  if (window.appStore.socket) {
    if (window.appStore.socket.auth) {
      window.appStore.socket.auth.token = token;
    } else {
      window.appStore.socket.auth = {'token' : token};
    }
  }
}

function setTokenRenewal(expiryTime) {
  try {
    const buffer  = 60 * 1000;  // 1 minute before expiration
    const now     = Date.now();
    const delay   = Math.max(expiryTime*1000 - now - buffer, 0);  //To compute even if the delay is -ve, max is used.
    setTimeout(async () => {
      await getNewToken();
    }, delay);
  } catch (error) {
    console.log('Renewal Error: ', error);
  }
}

function cleanOldTokensInSessionStorage() {
  try {
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith("WS_TOKEN_")) {
        sessionStorage.removeItem(key);
      }
    }
  } catch (error) {
    console.log('Cleaning Error ', error);
  }
}

async function getNewToken() {
  try {
    const payload = {
      domain: window.LOGGED_IN_USER.domain,
      userId: window.LOGGED_IN_USER.user_id,
      email: window.LOGGED_IN_USER.email
    }
    const tokenResponse = await ApiClient.WebSockets.getWebSocketToken(payload);
    if (tokenResponse.success && tokenResponse?.token && tokenResponse?.expiryTime) {
      const uniqId = `WS_${crypto.randomUUID()}`;
      cleanOldTokensInSessionStorage();
      window.socketTokenId = uniqId;
      sessionStorage.setItem(`WS_TOKEN_${uniqId}`, tokenResponse.token);
      sessionStorage.setItem(`WS_TOKEN_EXPIRY_${uniqId}`, tokenResponse.expiryTime.toString());
      setTokenInSocketAuth(tokenResponse.token);
      setTokenRenewal(tokenResponse.expiryTime);
    }
    return tokenResponse.token;
  } catch (error) {
    console.log("Token Error ", error);
    return false;
  }
}

async function checkAndFetchToken() {
  try {
    const uniqId = window.socketTokenId;
    if (!uniqId) {
      return await getNewToken();
    } else {
      let token = sessionStorage.getItem(`WS_TOKEN_${uniqId}`);
      let expiryTime = parseInt(sessionStorage.getItem(`WS_TOKEN_EXPIRY_${uniqId}`) || "0", 10);
      if (!token || !expiryTime) {
        return await getNewToken();
      } else if (token && expiryTime) {
        const now = Date.now();
        const buffer = 60 * 1000;
        if (expiryTime*1000 > now + buffer) {
          setTokenInSocketAuth(token);
          setTokenRenewal(expiryTime);
          return token;
        } else {
          return await getNewToken();
        }
      }
    }
    return false;
  } catch (error) {
    console.log("Token Error ", error);
    return false;
  }
}

export async function initializeSocket(listId = '') {
  try {
    if (!process.env.REACT_APP_WEB_SOCKET_URL) {
      throw new Error("WebSocket URL is not defined");
    }

    if (window.appStore?.socket && window.appStore?.socket.connected) {
      console.log("WebSocket already connected, preventing duplicate connection.");
      return;
    }

    const token = await checkAndFetchToken();
    if ((!window?.appStore?.clientId && token) || !window.appStore.socket) {
      window.appStore.clientId = `client-${crypto.randomUUID()}`;

      window.appStore.socket = io(process.env.REACT_APP_WEB_SOCKET_URL, {
        path: "/ts-socket",
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 2000,
        transports: ['websocket', 'polling'],
        autoConnect: false,
        withCredentials: true,
        auth: {
          token: token
        }
      });
      window.appStore?.socket.connect();
      // checkProperConnectionViaPingPong();
    } else if (token && window.appStore?.socket && !window.appStore?.socket.connected) {
      window.appStore?.socket.connect();
      // checkProperConnectionViaPingPong();
    }

    window.appStore?.socket.on("connect", async () => {
      try {
        handleSocketConnection(listId);
        if (window.appStore.clientId) {
          await ApiClient.WebSockets.saveWebSocketClientId({client_id: window.appStore.clientId});
        }
      } catch (err) {
        console.log('Client Id saving Error: ', err);
      }
    });

    window.appStore?.socket.on("pong-check", () => {
      window.appStore.pongReceived = true;
      console.log("Pong received from server, socket is valid.");
    });

    // window.appStore?.socket.on("connect_error", async (err) => {
    //   console.log("Connection error from WebSocket server ", err);
    //   if (err.message === 'JWT has expired.' || err.message === "Auth token missing or expired") {
    //     try {
    //       const token = await getNewToken();
    //       if (token) {
    //         if (!window.appStore.clientId) {
    //           window.appStore.clientId = `client-${crypto.randomUUID()}`;
    //         }

    //         if (!window.appStore.socket) {
    //           window.appStore.socket = io(process.env.REACT_APP_WEB_SOCKET_URL, {
    //             path: "/ts-socket",
    //             reconnection: true,
    //             reconnectionAttempts: 5,
    //             reconnectionDelay: 2000,
    //             transports: ['websocket', 'polling'],
    //             autoConnect: false,
    //             withCredentials: true,
    //             auth: {
    //               token: token
    //             }
    //           });
    //         } else {
    //           setTokenInSocketAuth(token);
    //         }
    //         window.appStore?.socket.connect();
    //         // checkProperConnectionViaPingPong();
    //       }
    //     } catch (err) {
    //       console.log('Reconnection with new token failed');
    //     }
    //   } 
    // });

    window.appStore?.socket.on("disconnect", () => {
      console.log("Disconnected from WebSocket server. ");
      if (window.appStore.socket) {
        window.appStore.socket.connected = false;
      }
    });

    window.appStore?.socket.on("reconnect", (attempt) => {
      console.log(`Reconnected to WebSocket server after ${attempt} attempts`);
    });

    window.appStore?.socket.on('update-data-alert', (data) => {
      try {
        const { domain, listId, modifiedData, newRowsData, userName, isSubsheet, parentRowIds, fromWebFormOrEmail, clientId } = data;
        console.log("update-data-event from socket to frontend for domain: ", domain, listId);
        const updatedData = modifiedData != "" ? JSON.parse(decompressGzipBase64(modifiedData)) : {};
        const newRows = newRowsData != "" ? JSON.parse(decompressGzipBase64(newRowsData)) : [];
        const parentRows = parentRowIds != "" ? JSON.parse(parentRowIds) : [];
        handleUpdatedAndNewRowsDataFromSocket(domain, listId, updatedData, newRows, parentRows, userName, isSubsheet, fromWebFormOrEmail, clientId);
      } catch (error) {
        console.error("Error: ", error);
      }
    });

    window.appStore?.socket.on('update-subsheet-data-alert', (data) => {
      try {
        const { domain, modifiedSubsheetData, listId, parentRowId, userName, clientId } = data;
        console.log("update-subsheet-data-event from socket to frontend for domain: ", domain, listId);
        const subsheetData = modifiedSubsheetData != "" ? JSON.parse(decompressGzipBase64(modifiedSubsheetData)) : {};
        handleSubsheetDataUpdationThroughFormViaSocket(domain, subsheetData, listId, parentRowId, userName, clientId);
      } catch (error) {
        console.error("Error: ", error);
      }
    });

    window.appStore?.socket.on('update-comments-alert', (data) => {
      try {
        const { domain, listId, userName, commentsData, commentsCount, rowId, uniqueId, clientId } = data;
        console.log("update-comments-event from socket to frontend for domain: ", domain, listId);
        const comments = commentsData != "" ? JSON.parse(decompressGzipBase64(commentsData)) : [];
        handleCommentsUpdationFromSocket(domain, listId, rowId, uniqueId, comments, commentsCount, userName, clientId);
      } catch (error) {
        console.error("Error: ", error);
      }
    });

    window.appStore?.socket.on('update-deleted-rows-alert', (data) => {
      try {
        const { domain, listId, rowIdsList, userName, isSubsheet, parentRowIds, clientId } = data;
        console.log("update-deleted-rows-event from socket to frontend for domain: ", domain, listId);
        const deletedRowIds = rowIdsList != "" ? JSON.parse(decompressGzipBase64(rowIdsList)) : [];
        const parentRows = parentRowIds != "" ? JSON.parse(parentRowIds) : [];
        handleRowDeletionFromSocket(deletedRowIds, parentRows, domain, listId, userName, isSubsheet, clientId);
      } catch (error) {
        console.error("Error: ", error);
      }
    });

        window.appStore?.socket.on('add-edit-column-alert', (data) => {
          try {
            const { domain, listId, columnData, decimals, userName, isSubsheet, parentListId, isOldColumn, shouldRefreshData, shouldRedrawRefreshedRows, clientId } = data;
            console.log("add-edit-column-event from socket to frontend for domain: ", domain, listId);
            const column = columnData != "" ? JSON.parse(decompressGzipBase64(columnData)) : {};
            handleAddEditColumnFromSocket(domain, listId, column, decimals, userName, isSubsheet, parentListId, isOldColumn, shouldRefreshData, shouldRedrawRefreshedRows, clientId);
          } catch (error) {
            console.error("Error: ", error);
          }
        });

        window.appStore?.socket.on('delete-column-alert', (data) => {
          try {
            const { domain, listId, columnId, isSubsheet, parentListId, userName, clientId} = data;
            console.log("delete-column-event from socket to frontend for domain: ", domain, listId);
            handleDeleteColumnFromSocket(domain, listId, columnId, isSubsheet, parentListId, userName, clientId);
          } catch (error) {
            console.error("Error: ", error);
          }
        });

    window.appStore?.socket.on("error", (error) => {
      console.error("WebSocket error:", error);
    });
  } catch (e) {
    console.error("Failed to initialize WebSocket:", e);
  }
}